<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Popover from 'dashboard/components-next/popover/Popover.vue';

const props = defineProps({
  align: {
    type: String,
    default: 'end',
  },
});

const { t } = useI18n();
const store = useStore();

const formState = reactive({
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  inboxId: null,
  message: '',
  assigneeId: null,
  customAttributes: {},
});

const isSubmitting = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');

const customAttrDefs = computed(() => {
  const getter = store.getters['attributes/getAttributesByModel'];
  if (!getter) return [];
  return (getter('conversation_attribute') || []).map(attr => ({
    ...attr,
    displayName: attr.attribute_display_name,
    displayType: attr.attribute_display_type,
    key: attr.attribute_key,
    values: attr.attribute_values || [],
  }));
});

const initCustomAttributes = () => {
  customAttrDefs.value.forEach(attr => {
    if (!(attr.key in formState.customAttributes)) {
      formState.customAttributes[attr.key] =
        attr.displayType === 'checkbox' ? false : '';
    }
  });
};

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('agents/get'),
      store.dispatch('attributes/get'),
    ]);
    initCustomAttributes();
  } catch (e) {
    // Silently ignore fetch errors
  }
});

// Re-init when custom attr defs change
watch(customAttrDefs, () => {
  initCustomAttributes();
});

const isValid = computed(() => {
  return (
    formState.contactName.trim() &&
    formState.inboxId &&
    formState.message.trim()
  );
});

const agentOptions = computed(() =>
  (agents.value || []).map(a => ({
    id: a.id,
    name: a.name || a.email || a.id,
  }))
);

const inboxOptions = computed(() => (inboxes.value || []).map(i => ({
  id: i.id,
  name: i.name || `Inbox ${i.id}`,
})));

const hasCustomFields = computed(() => customAttrDefs.value.length > 0);

const renderCustomField = attr => {
  switch (attr.displayType) {
    case 'list':
      return 'select';
    case 'checkbox':
      return 'checkbox';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'link':
      return 'url';
    case 'text':
    default:
      return 'text';
  }
};

const findOrCreateContact = async () => {
  // Search by email first, then by phone, then by name
  const searchQuery =
    formState.contactEmail.trim() ||
    formState.contactPhone.trim() ||
    formState.contactName.trim();

  if (searchQuery) {
    try {
      const {
        data: { payload },
      } = await ContactAPI.search(searchQuery, 1, 'name', '');
      const matches = payload || [];

      // Try exact match on email
      if (formState.contactEmail.trim()) {
        const emailMatch = matches.find(
          c => c.email?.toLowerCase() === formState.contactEmail.trim().toLowerCase()
        );
        if (emailMatch) return emailMatch.id;
      }

      // Try exact match on phone
      if (formState.contactPhone.trim()) {
        const phoneMatch = matches.find(
          c => c.phone_number === formState.contactPhone.trim()
        );
        if (phoneMatch) return phoneMatch.id;
      }

      // Try exact match on name + email/phone (partial match, highest confidence)
      if (matches.length > 0 && formState.contactEmail.trim()) {
        const nameMatch = matches.find(
          c => c.name?.toLowerCase() === formState.contactName.trim().toLowerCase()
        );
        if (nameMatch) return nameMatch.id;
      }
    } catch (e) {
      // Search failed, fall through to creation
    }
  }

  // Not found — create new contact
  const contactPayload = {
    name: formState.contactName.trim(),
  };
  if (formState.contactEmail.trim()) {
    contactPayload.email = formState.contactEmail.trim();
  }
  if (formState.contactPhone.trim()) {
    contactPayload.phone_number = formState.contactPhone.trim();
  }

  const {
    data: {
      payload: { contact: newContact },
    },
  } = await ContactAPI.create(contactPayload);
  return newContact.id;
};

const handleSubmit = async hidePopover => {
  if (!isValid.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    // 1. Find existing or create new contact
    const contactId = await findOrCreateContact();

    // 2. Create conversation
    const convPayload = new FormData();
    convPayload.append('inbox_id', Number(formState.inboxId));
    convPayload.append('contact_id', Number(contactId));
    convPayload.append('source_id', crypto.randomUUID?.() || Date.now().toString());
    convPayload.append('message[content]', formState.message.trim());
    if (formState.assigneeId) {
      convPayload.append('assignee_id', Number(formState.assigneeId));
    }

    const { data: convData } = await ConversationApi.create(convPayload);
    const conversationId = convData.id;
    const accountId = convData.account_id;

    // 3. Write custom attributes (non-empty values only) — non-blocking
    const customAttrValues = {};
    Object.entries(formState.customAttributes).forEach(([key, value]) => {
      if (value !== '' && value !== false && value !== null) {
        customAttrValues[key] = value;
      }
    });

    try {
      if (Object.keys(customAttrValues).length > 0) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes: customAttrValues,
        });
      }
    } catch (attrError) {
      // Custom attributes write failed — log but don't block the success flow
      console.warn('[NewTicketForm] Custom attributes write failed:', attrError);
    }

    // 4. Success - show toast with link to ticket
    useAlert(t('NEW_TICKET_FORM.SUCCESS'), {
      type: 'link',
      to: `/app/accounts/${accountId}/conversations/${conversationId}`,
      message: t('NEW_TICKET_FORM.GO_TO_TICKET'),
    });

    // Reset form
    resetForm();
    hidePopover();
  } catch (error) {
    // Only conversation creation failure reaches here
    const errStatus = error?.response?.status;
    const errMsg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      t('NEW_TICKET_FORM.ERROR');
    console.error('[NewTicketForm] Submit error:', errStatus, errMsg, error);
    useAlert(`${errMsg}` + (errStatus ? ` (HTTP ${errStatus})` : ''));
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  formState.contactName = '';
  formState.contactEmail = '';
  formState.contactPhone = '';
  formState.inboxId = null;
  formState.message = '';
  formState.assigneeId = null;
  Object.keys(formState.customAttributes).forEach(k => {
    formState.customAttributes[k] =
      customAttrDefs.value.find(a => a.key === k)?.displayType === 'checkbox'
        ? false
        : '';
  });
};
</script>

<template>
  <Popover :align="props.align">
    <template #default="{ isOpen }">
      <slot name="trigger" :isOpen="isOpen" />
    </template>

    <template #content="{ hide }">
      <div
        class="w-full md:w-[34rem] flex flex-col max-h-[calc(100vh-8rem)]"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-5 py-4 border-b border-n-strong flex-shrink-0"
        >
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('NEW_TICKET_FORM.TITLE') }}
          </h3>
          <button
            type="button"
            class="flex items-center justify-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="hide"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Form Body -->
        <div class="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
          <!-- Customer Name -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ t('NEW_TICKET_FORM.CONTACT_NAME') }}
              <span class="text-n-ruby-9">*</span>
            </label>
            <input
              v-model="formState.contactName"
              type="text"
              class="w-full h-9 px-3 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:border-n-blue-9 transition-colors"
              :placeholder="t('NEW_TICKET_FORM.CONTACT_NAME')"
            />
          </div>

          <!-- Contact Email -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ t('NEW_TICKET_FORM.CONTACT_EMAIL') }}
            </label>
            <input
              v-model="formState.contactEmail"
              type="email"
              class="w-full h-9 px-3 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:border-n-blue-9 transition-colors"
              placeholder="customer@example.com"
            />
          </div>

          <!-- Contact Phone -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ t('NEW_TICKET_FORM.CONTACT_PHONE') }}
            </label>
            <input
              v-model="formState.contactPhone"
              type="tel"
              class="w-full h-9 px-3 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:border-n-blue-9 transition-colors"
              placeholder="+886 ..."
            />
          </div>

          <!-- Inbox -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ t('NEW_TICKET_FORM.INBOX') }}
              <span class="text-n-ruby-9">*</span>
            </label>
            <select
              v-model="formState.inboxId"
              class="w-full h-9 px-3 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-9 transition-colors"
            >
              <option :value="null" disabled>
                {{ t('NEW_TICKET_FORM.INBOX_PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in inboxOptions"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>

          <!-- Message -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ t('NEW_TICKET_FORM.MESSAGE') }}
              <span class="text-n-ruby-9">*</span>
            </label>
            <textarea
              v-model="formState.message"
              rows="3"
              class="w-full px-3 py-2 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:border-n-blue-9 transition-colors resize-y min-h-[80px]"
              :placeholder="t('NEW_TICKET_FORM.MESSAGE_PLACEHOLDER')"
            />
          </div>

          <!-- Custom Fields from Chatwoot Custom Attributes -->
          <template v-if="hasCustomFields">
            <div
              class="pt-2 border-t border-n-strong text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
            >
              {{ t('NEW_TICKET_FORM.CUSTOM_FIELDS') }}
            </div>

            <div
              v-for="attr in customAttrDefs"
              :key="attr.key"
              class="flex flex-col gap-1"
            >
              <label class="text-sm font-medium text-n-slate-11">
                {{ attr.displayName }}
              </label>

              <!-- Text / Number / Date / Link / Text -->
              <input
                v-if="renderCustomField(attr) !== 'select' && renderCustomField(attr) !== 'checkbox'"
                v-model="formState.customAttributes[attr.key]"
                :type="renderCustomField(attr)"
                :placeholder="attr.displayName"
                class="w-full h-9 px-3 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 outline-none focus:border-n-blue-9 transition-colors"
              />

              <!-- Select (list type) -->
              <select
                v-else-if="renderCustomField(attr) === 'select'"
                v-model="formState.customAttributes[attr.key]"
                class="w-full h-9 px-3 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-9 transition-colors"
              >
                <option value="" />
                <option
                  v-for="val in attr.values"
                  :key="val"
                  :value="val"
                >
                  {{ val }}
                </option>
              </select>

              <!-- Checkbox -->
              <label
                v-else-if="renderCustomField(attr) === 'checkbox'"
                class="flex items-center gap-2 cursor-pointer"
              >
                <input
                  v-model="formState.customAttributes[attr.key]"
                  type="checkbox"
                  class="size-4 rounded border-n-strong text-n-blue-9 focus:ring-n-blue-9"
                />
                <span class="text-sm text-n-slate-12">{{ attr.displayName }}</span>
              </label>
            </div>
          </template>

          <!-- Assignee -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ t('NEW_TICKET_FORM.ASSIGNEE') }}
            </label>
            <select
              v-model="formState.assigneeId"
              class="w-full h-9 px-3 rounded-lg border border-n-strong bg-n-alpha-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-9 transition-colors"
            >
              <option :value="null">
                {{ t('NEW_TICKET_FORM.ASSIGNEE_AUTO') }}
              </option>
              <option
                v-for="agent in agentOptions"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-end gap-2 px-5 py-3 border-t border-n-strong flex-shrink-0"
        >
          <button
            type="button"
            class="h-9 px-4 rounded-lg text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            @click="hide"
          >
            {{ t('NEW_TICKET_FORM.CANCEL') }}
          </button>
          <button
            type="button"
            class="h-9 px-5 rounded-lg text-sm font-medium text-white bg-n-blue-9 hover:bg-n-blue-10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!isValid || isSubmitting"
            @click="handleSubmit(hide)"
          >
            {{ isSubmitting ? '...' : t('NEW_TICKET_FORM.SUBMIT') }}
          </button>
        </div>
      </div>
    </template>
  </Popover>
</template>
